import { CircleCheck, OctagonAlert } from 'lucide-react';
import { memo } from 'react';

import { Spinner } from '@apps/web/components/ui/spinner';
import { type V3ChatActions } from '@apps/web/features/phoebe_chat_v3/model/actions';
import { getV3ApprovalRoute } from '@apps/web/features/phoebe_chat_v3/model/approval_routing';
import { type V3PresentationContext } from '@apps/web/features/phoebe_chat_v3/model/presentation_context';
import { type V3ToolEntry } from '@apps/web/features/phoebe_chat_v3/model/transcript';
import { V3ChatAttachmentCard } from '@apps/web/features/phoebe_chat_v3/ui/chat_attachment_card';
import {
    V3CommsUi,
    V3DynamicUi,
} from '@apps/web/features/phoebe_chat_v3/ui/dynamic_ui';
import { V3GenericApprovalCard } from '@apps/web/features/phoebe_chat_v3/ui/generic_approval_card';
import { V3OutreachLinkChip } from '@apps/web/features/phoebe_chat_v3/ui/outreach_link_chip';
import { V3QuestionForm } from '@apps/web/features/phoebe_chat_v3/ui/question_form';

export const V3ToolRow = memo(function V3ToolRow({
    entry,
    transcriptIndex = 0,
    presentationContext,
    actions,
}: {
    readonly entry: V3ToolEntry;
    readonly transcriptIndex?: number;
    readonly presentationContext: V3PresentationContext | null;
    readonly actions: V3ChatActions;
}) {
    const callId = entry.callId;
    const isAnswering = callId
        ? actions.answeringQuestionCallIds[callId] === true
        : false;
    const approvalRoute = getV3ApprovalRoute(entry);

    // Valid communications payloads get the native comms card, which owns
    // its own truthful approval labels and settled receipt.
    if (
        approvalRoute === 'communications' &&
        entry.commsPresentation !== null
    ) {
        return (
            <div className="w-full max-w-[700px] text-sm text-[#002429]">
                <V3CommsUi entry={entry} actions={actions} />
            </div>
        );
    }

    if (approvalRoute === 'generic' && callId !== null) {
        return (
            <V3GenericApprovalCard
                entry={entry}
                callId={callId}
                actions={actions}
            />
        );
    }

    const blockedApprovalMessage =
        approvalRoute === 'blocked_communications'
            ? 'This communication cannot be reviewed because its recipient or message details are incomplete. Ask Phoebe to try again.'
            : null;

    const activityIcon = entry.error ? (
        <OctagonAlert />
    ) : entry.isComplete ? (
        <CircleCheck />
    ) : (
        <Spinner className="motion-reduce:animate-none" />
    );

    return (
        <div className="max-w-[700px] space-y-3 text-sm text-[#002429]">
            {entry.activityLabel ? (
                <div
                    data-testid="v3-tool-activity"
                    className="flex min-w-0 items-center gap-2 py-0.5 text-sm leading-5 text-[#617578]"
                >
                    <span
                        aria-hidden="true"
                        className={
                            entry.error
                                ? 'shrink-0 text-[#A8002E] [&>svg]:size-4'
                                : entry.isComplete
                                  ? 'shrink-0 text-[#05614F] [&>svg]:size-4'
                                  : 'shrink-0 text-[#5533EE] [&>svg]:size-4'
                        }
                    >
                        {activityIcon}
                    </span>
                    <span className="min-w-0">{entry.activityLabel}</span>
                </div>
            ) : null}
            {entry.error ? (
                <p className="pl-6 text-xs leading-4 text-[#A8002E]">
                    {entry.error}
                </p>
            ) : null}
            {blockedApprovalMessage ? (
                <p
                    role="alert"
                    className="pl-6 text-xs leading-4 text-[#A8002E]"
                >
                    {blockedApprovalMessage}
                </p>
            ) : null}
            {(entry.dataPresentation !== null ||
                entry.outreachPresentation !== null) &&
            presentationContext !== null ? (
                <V3DynamicUi
                    entry={entry}
                    transcriptIndex={transcriptIndex}
                    context={presentationContext}
                    actions={actions}
                />
            ) : null}
            {entry.chatAttachment !== null && presentationContext !== null ? (
                <V3ChatAttachmentCard
                    attachment={entry.chatAttachment}
                    context={presentationContext}
                />
            ) : null}
            {entry.outreachLink !== null ? (
                <V3OutreachLinkChip
                    outreachId={entry.outreachLink.outreachId}
                />
            ) : null}
            {callId !== null &&
            entry.questions.length > 0 &&
            actions.onQuestionAnswer ? (
                <V3QuestionForm
                    key={`${entry.id}:${entry.revision}`}
                    questions={entry.questions}
                    presentation={entry.questionPresentation}
                    disabled={isAnswering}
                    onSubmit={(payload) => {
                        void actions.onQuestionAnswer?.(callId, payload);
                    }}
                />
            ) : null}
        </div>
    );
});
